/* Aurio — CSV import.

   Bank exports are wildly inconsistent, so everything here handles the real
   world rather than an idealised file: quoted fields with embedded commas and
   newlines, three delimiters, Excel's BOM, headerless files, four date
   formats, and both amount conventions (a signed column, or separate
   debit/credit columns).

   Pure and dependency-free — no React, no app state — so it is directly
   unit-testable (see csv.test.js). Deliberately no CSV library: the app has no
   runtime dependencies beyond React and Supabase, and this is ~200 lines for
   one screen.

   Dates are produced with localISO from money.js, never toISOString(): at
   UTC+13 the latter reports the previous day for most of the working day (the
   bug fixed in v0.24.0), which here would silently shift every row in a file. */

import { localISO } from "./money.js";

/* ── low-level parsing ───────────────────────────────────────────────────── */

const DELIMITERS = [",", ";", "\t"];

/* Counts a delimiter only OUTSIDE quotes — a description like
   "COUNTDOWN, AUCKLAND" must not make commas look more common than they are. */
function countOutsideQuotes(line, ch) {
  let n = 0, inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') { if (inQ && line[i + 1] === '"') i++; else inQ = !inQ; continue; }
    if (c === ch && !inQ) n++;
  }
  return n;
}

export function detectDelimiter(text) {
  const firstLine = (text.split(/\r?\n/).find(l => l.trim()) || "");
  let best = ",", bestCount = 0;
  for (const d of DELIMITERS) {
    const n = countOutsideQuotes(firstLine, d);
    if (n > bestCount) { best = d; bestCount = n; }
  }
  return best;
}

/* A single pass over the text, so quoted newlines are handled naturally
   rather than by splitting on lines first and trying to stitch them back. */
export function parseCsv(text) {
  const clean = String(text || "").replace(/^﻿/, ""); // Excel writes a BOM
  const delimiter = detectDelimiter(clean);
  const rows = [];
  let row = [], field = "", inQ = false;

  const endField = () => { row.push(field); field = ""; };
  const endRow = () => { endField(); if (row.some(c => c.trim() !== "")) rows.push(row); row = []; };

  for (let i = 0; i < clean.length; i++) {
    const c = clean[i];
    if (inQ) {
      if (c === '"') {
        if (clean[i + 1] === '"') { field += '"'; i++; } // escaped ""
        else inQ = false;
      } else field += c;
      continue;
    }
    if (c === '"') { inQ = true; continue; }
    if (c === delimiter) { endField(); continue; }
    if (c === "\r") { if (clean[i + 1] === "\n") i++; endRow(); continue; }
    if (c === "\n") { endRow(); continue; }
    field += c;
  }
  if (field !== "" || row.length) endRow();

  return { rows: rows.map(r => r.map(c => c.trim())), delimiter };
}

/* ── amounts ─────────────────────────────────────────────────────────────── */

/* Handles "1,234.56", "1.234,56" (European/BRL), "(12.34)" for negative,
   a leading currency symbol, and a trailing minus. Returns null when the
   string holds no number at all, so callers can report the row rather than
   silently importing a zero. */
export function parseAmount(raw) {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (!s) return null;

  let negative = false;
  if (/^\(.*\)$/.test(s)) { negative = true; s = s.slice(1, -1); }   // (12.34)
  if (/-\s*$/.test(s)) { negative = true; s = s.replace(/-\s*$/, ""); } // 12.34-
  s = s.replace(/[^\d.,\-]/g, "");                                    // strip R$, $, NZ$…
  if (s.startsWith("-")) { negative = true; s = s.slice(1); }
  s = s.replace(/-/g, "");
  if (!s || !/\d/.test(s)) return null;

  const lastComma = s.lastIndexOf(",");
  const lastDot = s.lastIndexOf(".");
  if (lastComma > -1 && lastDot > -1) {
    // Whichever separator comes last is the decimal one.
    if (lastComma > lastDot) s = s.replace(/\./g, "").replace(",", ".");
    else s = s.replace(/,/g, "");
  } else if (lastComma > -1) {
    // Comma alone: decimal if it leaves 1-2 trailing digits, else thousands.
    s = /,\d{1,2}$/.test(s) ? s.replace(",", ".") : s.replace(/,/g, "");
  }

  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return negative ? -n : n;
}

/* ── dates ───────────────────────────────────────────────────────────────── */

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
/* NZ/AU/UK/most of Europe write day first; the US writes month first. */
const MDY_CURRENCIES = ["USD"];

const iso = (y, m, d) => {
  if (!(m >= 1 && m <= 12) || !(d >= 1 && d <= 31)) return null;
  const dt = new Date(y, m - 1, d, 12);
  // Rejects impossible dates that JS would silently roll over (31 Feb → 3 Mar).
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null;
  return localISO(dt);
};
const fullYear = (y) => (y < 100 ? (y < 70 ? 2000 + y : 1900 + y) : y);

/* Resolve day/month order across the WHOLE file before parsing any row.
   "03/04/2026" is 3 April in Auckland and 4 March in Chicago; guessing per-row
   would produce a file with mixed interpretations. Returns "DMY" | "MDY". */
export function detectDateOrder(samples, currency) {
  let sawDayFirst = false, sawMonthFirst = false;
  for (const s of samples) {
    const m = String(s || "").match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
    if (!m) continue;
    const a = +m[1], b = +m[2];
    if (a > 12 && b <= 12) sawDayFirst = true;
    if (b > 12 && a <= 12) sawMonthFirst = true;
  }
  if (sawDayFirst && !sawMonthFirst) return "DMY";
  if (sawMonthFirst && !sawDayFirst) return "MDY";
  // Genuinely ambiguous (or contradictory): fall back to the user's locale.
  return MDY_CURRENCIES.includes(currency) ? "MDY" : "DMY";
}

export function parseDate(raw, order = "DMY") {
  const s = String(raw || "").trim();
  if (!s) return null;

  let m = s.match(/^(\d{4})[/\-.](\d{1,2})[/\-.](\d{1,2})$/);        // 2026-08-04
  if (m) return iso(+m[1], +m[2], +m[3]);

  m = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);          // 04/08/2026
  if (m) {
    const y = fullYear(+m[3]);
    return order === "MDY" ? iso(y, +m[1], +m[2]) : iso(y, +m[2], +m[1]);
  }

  m = s.match(/^(\d{1,2})[\s\-]([A-Za-z]{3,})[\s\-](\d{2,4})$/);     // 4 Aug 2026
  if (m) {
    const mi = MONTHS.indexOf(m[2].slice(0, 3).toLowerCase());
    return mi > -1 ? iso(fullYear(+m[3]), mi + 1, +m[1]) : null;
  }

  m = s.match(/^([A-Za-z]{3,})[\s\-](\d{1,2}),?[\s\-](\d{2,4})$/);   // Aug 4, 2026
  if (m) {
    const mi = MONTHS.indexOf(m[1].slice(0, 3).toLowerCase());
    return mi > -1 ? iso(fullYear(+m[3]), mi + 1, +m[2]) : null;
  }
  return null;
}

/* ── column detection ────────────────────────────────────────────────────── */

const HEADER_PATTERNS = {
  date: /^(date|when|posted|transaction\s*date|processed)/i,
  debit: /^(debit|withdrawal|money\s*out|paid\s*out|spend)/i,
  credit: /^(credit|deposit|money\s*in|paid\s*in|received)/i,
  amount: /^(amount|value|transaction\s*amount|total)/i,
  description: /^(description|details|payee|particulars|narrative|reference|memo|merchant|name)/i,
};

const looksLikeHeader = (row) =>
  row.some(c => Object.values(HEADER_PATTERNS).some(re => re.test(c)));

/* Header keywords first; content sniffing when a bank ships no header row.
   `balance` is deliberately NOT treated as an amount candidate — it is
   numeric in every row and would otherwise win the sniffing contest. */
export function detectColumns(rows, currency) {
  if (!rows.length) return null;
  const header = looksLikeHeader(rows[0]) ? rows[0] : null;
  const body = header ? rows.slice(1) : rows;
  if (!body.length) return null;

  const width = Math.max(...rows.map(r => r.length));
  const col = { date: -1, amount: -1, debit: -1, credit: -1, description: -1, hasHeader: Boolean(header) };

  if (header) {
    header.forEach((h, i) => {
      for (const key of ["date", "debit", "credit", "amount", "description"]) {
        if (col[key] === -1 && HEADER_PATTERNS[key].test(h)) col[key] = i;
      }
    });
    // "Balance" columns often also match /amount|total/ loosely; drop any
    // amount column whose header actually says balance.
    if (col.amount > -1 && /balance/i.test(header[col.amount] || "")) col.amount = -1;
  }

  const sample = body.slice(0, 40);
  const score = (i, fn) => sample.reduce((n, r) => n + (fn(r[i]) ? 1 : 0), 0);

  if (col.date === -1) {
    let best = -1, bestN = 0;
    for (let i = 0; i < width; i++) {
      const n = score(i, v => parseDate(v, "DMY") != null);
      if (n > bestN) { best = i; bestN = n; }
    }
    if (bestN >= Math.max(1, sample.length * 0.6)) col.date = best;
  }

  if (col.amount === -1 && col.debit === -1) {
    let best = -1, bestN = 0;
    for (let i = 0; i < width; i++) {
      if (i === col.date) continue;
      if (header && /balance/i.test(header[i] || "")) continue;
      const n = score(i, v => parseAmount(v) != null);
      if (n > bestN) { best = i; bestN = n; }
    }
    if (bestN >= Math.max(1, sample.length * 0.6)) col.amount = best;
  }

  if (col.description === -1) {
    // The most consistently non-numeric, non-empty column.
    let best = -1, bestN = 0;
    for (let i = 0; i < width; i++) {
      if (i === col.date || i === col.amount || i === col.debit || i === col.credit) continue;
      const n = score(i, v => v && parseAmount(v) == null && parseDate(v, "DMY") == null);
      if (n > bestN) { best = i; bestN = n; }
    }
    if (bestN > 0) col.description = best;
  }

  return col;
}

/* ── merchants & categories ──────────────────────────────────────────────── */

/* A deliberately small, honest NZ/AU seed — not an attempt at completeness.
   The learning (state.merchantRules) is what makes categorising good; this
   just avoids a wall of "Other" on someone's very first import. */
export const MERCHANT_HINTS = [
  [/countdown|woolworths|new world|pak\s*n\s*save|paknsave|four square|coles|aldi|iga/i, "groceries"],
  [/z energy|\bbp\b|mobil|caltex|gull|waitomo|npd|7-eleven|uber trip|ubereats?|didi|ola|at hop|myki|opal/i, "transport"],
  [/mcdonald|kfc|burger|domino|pizza|subway|starbucks|cafe|coffee|restaurant|doordash|menulog|deliveroo/i, "eating"],
  [/spark|one nz|vodafone|2degrees|skinny|telstra|optus|mercury|contact energy|genesis|meridian|electric kiwi|nova energy|watercare/i, "utilities"],
  [/netflix|spotify|disney|neon|sky tv|prime video|apple\.com\/bill|google storage|youtube premium|patreon|icloud/i, "subs"],
  [/aa insurance|state insurance|tower|ami |southern cross|nib|partners life|allianz|medibank|bupa/i, "insurance"],
  [/les mills|anytime fitness|jetts|snap fitness|city fitness|gym|chemist warehouse|my muscle/i, "fitness"],
  [/kmart|warehouse|briscoes|farmers|mitre 10|bunnings|noel leeming|jb hi-fi/i, "personal"],
  [/air new zealand|jetstar|qantas|booking\.com|airbnb|expedia|flight ?centre/i, "trip"],
  [/wise|remitly|western union|orbit remit|xe\.com/i, "family"],
];

/* Collapses card-processor noise so the same shop is one key:
   "COUNTDOWN 1234 AUCKLAND 04AUG" and "COUNTDOWN 0567 WELLINGTON" → "countdown" */
export function merchantKey(description) {
  let s = String(description || "").toLowerCase();
  s = s.replace(/\b\d{1,2}[/\-.]\d{1,2}([/\-.]\d{2,4})?\b/g, " ");   // embedded dates
  s = s.replace(/\b\d{1,2}[a-z]{3}\b/g, " ");                        // 04aug
  s = s.replace(/\b(card|ref|reference|eftpos|visa|mastercard|debit|credit|purchase|payment|pos|tfr|transfer|value date)\b/g, " ");
  s = s.replace(/[^a-z\s]/g, " ");                                   // digits & punctuation
  s = s.replace(/\s+/g, " ").trim();

  /* How many words identify the shop varies, and the branch/suburb that
     follows must not get in: "COUNTDOWN 1234 AUCKLAND" and "COUNTDOWN 0567
     WELLINGTON" have to collapse to the same key or nothing is ever learned.
     A distinctive first word stands alone; a short or generic one needs its
     neighbour ("z energy", "new world"). Imperfect by nature — two different
     cafés both starting "cafe" will share a key — but wrong-in-the-safe-
     direction: the user simply recategorises, and that correction is stored
     against the shared key. */
  const words = s.split(" ").filter(Boolean);
  if (!words.length) return "";
  return words[0].length >= 5 ? words[0] : words.slice(0, 2).join(" ");
}

export function guessCategory(description, rules = {}, categoryIds = null) {
  const key = merchantKey(description);
  const learned = key && rules[key];
  // A learned rule always wins — the user corrected this exact merchant before.
  if (learned && (!categoryIds || categoryIds.includes(learned))) return learned;
  for (const [re, cat] of MERCHANT_HINTS) {
    if (re.test(String(description || ""))) {
      if (!categoryIds || categoryIds.includes(cat)) return cat;
    }
  }
  return "other";
}

/* ── rows → expenses ─────────────────────────────────────────────────────── */

const dupeKey = (date, amount, note) =>
  `${date}|${amount.toFixed(2)}|${String(note || "").trim().toLowerCase()}`;

/* Money OUT only. Aurio tracks expenses; income is a separate setting, so
   credits are counted and reported rather than imported. Duplicates are
   expected, not exceptional — re-importing an overlapping statement is the
   normal way people use this. Unparsable rows are returned with their line
   number so nothing disappears silently. */
export function toExpenses(rows, col, opts = {}) {
  const { rules = {}, existing = [], importId = "", dateOrder = "DMY", categoryIds = null } = opts;
  const body = col.hasHeader ? rows.slice(1) : rows;
  const seen = new Set(existing.map(e => dupeKey(e.date, Number(e.amount) || 0, e.note)));

  const ready = [], unparsable = [];
  let duplicates = 0, skippedIncome = 0;

  body.forEach((r, i) => {
    const lineNo = i + 1 + (col.hasHeader ? 1 : 0);
    const date = parseDate(r[col.date], dateOrder);
    const note = (col.description > -1 ? r[col.description] : "") || "";

    let amount = null;
    if (col.debit > -1 || col.credit > -1) {
      const debit = col.debit > -1 ? parseAmount(r[col.debit]) : null;
      const credit = col.credit > -1 ? parseAmount(r[col.credit]) : null;
      if (debit != null && debit !== 0) amount = -Math.abs(debit);
      else if (credit != null && credit !== 0) amount = Math.abs(credit);
    } else if (col.amount > -1) {
      amount = parseAmount(r[col.amount]);
    }

    if (!date || amount == null || amount === 0) {
      if (r.join("").trim()) unparsable.push({ line: lineNo, raw: r.join(" ").slice(0, 80) });
      return;
    }
    if (amount > 0) { skippedIncome++; return; }  // a credit/deposit

    const spend = Math.abs(amount);
    const key = dupeKey(date, spend, note);
    if (seen.has(key)) { duplicates++; return; }
    seen.add(key);

    ready.push({
      date, amount: spend, note: String(note).slice(0, 120).trim(),
      category: guessCategory(note, rules, categoryIds),
      importId,
    });
  });

  ready.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  return { ready, duplicates, skippedIncome, unparsable };
}
